import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
} from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import {
  PortalBusServicingCyclesAllQueryGQLType,
  usePortalBusServicingCyclesAllQuery,
} from '~/backup/shared/deals.gql.generated';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  GlobalNav,
  GlobalNavRouterLinkItem,
} from '~/packages/common-frontend/layout';
import { useLayoutContext } from '~/packages/common-frontend/layout/LayoutProvider';

type Cycle = PortalBusServicingCyclesAllQueryGQLType['portalBusServicingCycles'][number];

/** One entry in the deal switcher: a deal (implementation) + its display name. */
type Deal = { implementationId: string; name: string };

/**
 * Collapse the caller's cycles to one entry per deal (implementation), keyed by
 * `implementationId` and labelled with the credit-facility name, sorted for a
 * stable dropdown order.
 */
function distinctDeals(cycles: Cycle[]): Deal[] {
  const byDeal = new Map<string, Deal>();
  cycles.forEach((c) => {
    if (!byDeal.has(c.implementationId)) {
      byDeal.set(c.implementationId, {
        implementationId: c.implementationId,
        name: c.creditFacilityName,
      });
    }
  });
  return Array.from(byDeal.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

/**
 * Dropdown that lists every deal the caller is a party to and navigates to the
 * chosen one, preserving the current deal sub-page (`subPath`) so switching
 * from one deal's Files view lands on the next deal's Files view. Shares the
 * `PortalBusServicingCyclesAll` operation (cache-first) with the Portfolio index.
 */
function DealSwitcher({
  implementationId,
  subPath,
}: {
  implementationId: string;
  subPath: string;
}): React.ReactElement {
  const navigate = useNavigate();
  const { data, loading, error } = usePortalBusServicingCyclesAllQuery({
    fetchPolicy: 'cache-first',
  });

  const deals = distinctDeals(data?.portalBusServicingCycles ?? []);
  const current = deals.find((d) => d.implementationId === implementationId);

  let label: React.ReactNode = 'Select deal';
  if (current) label = current.name;
  else if (loading) label = <Spinner size="xs" />;
  else if (error) label = 'Unavailable';

  return (
    <Menu matchWidth placement="bottom">
      <MenuButton
        as={Button}
        variant="outline"
        size="sm"
        width="100%"
        textAlign="left"
        fontWeight={500}
        rightIcon={<Icon icon="chevron-down" />}
        borderColor={colors.gray[200]}
      >
        <Text isTruncated>{label}</Text>
      </MenuButton>
      <MenuList maxH="20rem" overflowY="auto" zIndex="dropdown">
        {error ? (
          <Box px="0.75rem" py="0.5rem">
            <Text fontSize="0.8rem" color={colors.red[400]}>
              Could not load deals. Please try again.
            </Text>
          </Box>
        ) : (
          deals.map((d) => (
            <MenuItem
              key={d.implementationId}
              onClick={() => navigate(`/deals/${d.implementationId}${subPath}`)}
              fontWeight={d.implementationId === implementationId ? 600 : 400}
              fontSize="0.875rem"
            >
              {d.name}
            </MenuItem>
          ))
        )}
      </MenuList>
    </Menu>
  );
}

type Props = {
  /** The deal currently in view; scopes the Overview / Files links. */
  implementationId: string;
  /** Sub-page suffix ('' for Overview, '/files' for Files) carried by the switcher. */
  subPath: string;
  /** Where the "back" link points (portal index). */
  backTo: string;
  backLabel: string;
  /** Whether to surface the portfolio-level Strats link (lender only for now). */
  showStrats: boolean;
};

/**
 * Deal-scoped sidebar shown on `/deals/:implementationId[/files]` routes: a
 * back-link to the portal index, a deal switcher, and the per-deal sections
 * (Overview / Files, plus Strats for the lender). Reuses the shared `GlobalNav`
 * shell so collapse / mobile-drawer behaviour matches the flat portal nav; the
 * switcher only renders while the nav is expanded.
 */
export const BackupDealNav = ({
  implementationId,
  subPath,
  backTo,
  backLabel,
  showStrats,
}: Props): React.ReactElement => {
  const layout = useLayoutContext();
  return (
    <GlobalNav isFixed offsetHeaderCount={1}>
      <GlobalNavRouterLinkItem
        to={backTo}
        label={backLabel}
        icon={<Icon icon="arrow-left" />}
      />
      {layout.globalNavOpen && (
        <Box px="0.75rem" py="0.5rem">
          <DealSwitcher implementationId={implementationId} subPath={subPath} />
        </Box>
      )}
      <GlobalNavRouterLinkItem
        to={`/deals/${implementationId}`}
        end
        label="Overview"
        icon={<Icon icon="house" />}
      />
      <GlobalNavRouterLinkItem
        to={`/deals/${implementationId}/files`}
        label="Files"
        icon={<Icon icon="folder-open" />}
      />
      {showStrats && (
        <GlobalNavRouterLinkItem
          to="/strats"
          label="Strats"
          icon={<Icon icon="chart-mixed" />}
        />
      )}
    </GlobalNav>
  );
};
